import {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
  ReactNode,
} from 'react';

interface FlyoutContextType {
  activeItemId: string | null;
  anchorEl: HTMLElement | null;
  openFlyout: (itemId: string, element: HTMLElement) => void;
  closeFlyout: () => void;
  scheduleClose: () => void;
  cancelClose: () => void;
}

const FlyoutContext = createContext<FlyoutContextType | null>(null);

const OPEN_DELAY = 100; // Delay before opening flyout (prevents flickering on fast movement)
const CLOSE_DELAY = 150; // Delay before closing (allows moving to flyout)

export const FlyoutProvider = ({ children }: { children: ReactNode }) => {
  const [activeItemId, setActiveItemId] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const openTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const clearAllTimeouts = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    if (openTimeoutRef.current) {
      clearTimeout(openTimeoutRef.current);
      openTimeoutRef.current = null;
    }
  }, []);

  const closeFlyout = useCallback(() => {
    clearAllTimeouts();
    setActiveItemId(null);
    setAnchorEl(null);
  }, [clearAllTimeouts]);

  const openFlyout = useCallback(
    (itemId: string, element: HTMLElement) => {
      clearAllTimeouts();

      // If a different item is already open, switch immediately
      if (activeItemId && activeItemId !== itemId) {
        setActiveItemId(itemId);
        setAnchorEl(element);
        return;
      }

      // If same item, just cancel any pending close
      if (activeItemId === itemId) {
        return;
      }

      // Otherwise, add a small delay before opening
      openTimeoutRef.current = setTimeout(() => {
        setActiveItemId(itemId);
        setAnchorEl(element);
      }, OPEN_DELAY);
    },
    [activeItemId, clearAllTimeouts],
  );

  const scheduleClose = useCallback(() => {
    clearAllTimeouts();
    closeTimeoutRef.current = setTimeout(() => {
      setActiveItemId(null);
      setAnchorEl(null);
    }, CLOSE_DELAY);
  }, [clearAllTimeouts]);

  const cancelClose = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  // Cleanup timeouts on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
      if (openTimeoutRef.current) {
        clearTimeout(openTimeoutRef.current);
      }
    };
  }, []);

  return (
    <FlyoutContext.Provider
      value={{
        activeItemId,
        anchorEl,
        openFlyout,
        closeFlyout,
        scheduleClose,
        cancelClose,
      }}
    >
      {children}
    </FlyoutContext.Provider>
  );
};

export const useFlyout = () => {
  const context = useContext(FlyoutContext);
  if (!context) {
    throw new Error('useFlyout must be used within a FlyoutProvider');
  }
  return context;
};
